import * as THREE from 'three';
import { basic, emis, std } from '../materials.ts';
import { G, M, V, glowSprite } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * La Seta Madre (the Chasm's Mushroom Grotto guardian, docs/SIMA.md «Guardianes»): «Cuando se hincha, alejate.»
 * A mother-mushroom taller than a tower that never moves: a swollen pale stalk rooted in a torn cup, a ragged
 * skirt hanging under a vast slate-blue cap crusted with pale warts, its gills burning cyan underneath. A face
 * half-grown into the stalk, eyes shut, a slit of a mouth breathing light. Her brood grows round her foot in a
 * ring of small glowing caps, and threads of mycelium run out over the floor.
 *
 * How the integrator drives her (anim/creatures/spore-mother.ts):
 * - 'swell' (3.0 s, matching the sim's swell): the cap and stalk swell slowly to +35 % (the cap) while she shakes
 *   harder and harder; sample it at `swell × 3 s` for a swell fraction 0..1, or play it from the start.
 * - 'burst' (1.4 s): she bursts, the ring of spores (`ring`, built tiny) blows out round her and she sags back.
 * - 'idle'/'walk' are the same slow breathing (she does not move), 'attack' a puff toward a hero.
 *
 * Bones: stalk (pivot at the cup), cap (at the top of the stalk), `ring` (the burst's ring of spores, at the
 * ground, built at SPORE_RING_REST of its size). Faces +Z. Opaque shared materials, the spore mist the one
 * translucent unlit one, plus glow sprites.
 */

/** How small the burst's ring of spores is built (the burst clip scales `ring` by 1 / SPORE_RING_REST). */
export const SPORE_RING_REST = 0.02;
export const SPORE_MOTHER_HEIGHT = 2.75;

export const sporeMother = (o?: BaseOpts & { burst?: boolean }): Model => {
  const c = begin(o, 1.3, 2207);
  const { g, rng } = c;
  const k4 = kit(rng);

  const flesh = std('#5f6d78', { roughness: 0.7, bumpScale: 0.05 });
  const fleshD = std('#3a434b', { roughness: 0.8, bumpScale: 0.05 });
  const cap = std('#242138', { roughness: 0.5, bumpScale: 0.04, flatShading: true });
  const wart = std('#b9c4cc', { roughness: 0.85, flatShading: true });
  const gill = emis('#b5f0ff', '#4fd6ff', 1.25, { side: THREE.DoubleSide });
  const mist = basic('#7fe2ff', 0.22);

  const lathe = (pts: [number, number][], seg: number, mat: THREE.Material, parent: THREE.Object3D, jit = 0): THREE.Mesh => {
    const geo = new THREE.LatheGeometry(pts.map(([r, y]) => new THREE.Vector2(r, y)), seg);
    if (jit) {
      const pos = geo.attributes.position as THREE.BufferAttribute;
      const seen = new Map<string, number>();
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
        const key = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;
        let d = seen.get(key);
        if (d === undefined) {
          d = 1 + (rng() - 0.5) * jit;
          seen.set(key, d);
        }
        pos.setXYZ(i, x * d, y, z * d);
      }
      geo.computeVertexNormals();
    }
    return M(geo, mat, parent);
  };

  /** A small mushroom of her brood: a pale stalk, a dark cap with glowing gills. */
  const child = (parent: THREE.Object3D, x: number, z: number, h: number, lean: number): void => {
    const cg = G(parent, x, 0, z);
    cg.rotation.set(lean * Math.cos(x * 3), rng() * 6, lean * Math.sin(z * 3));
    const st = M(new THREE.CylinderGeometry(h * 0.1, h * 0.14, h, 6), flesh, cg);
    st.position.y = h / 2;
    const cp = M(new THREE.SphereGeometry(h * 0.36, 9, 5, 0, Math.PI * 2, 0, Math.PI / 2), cap, cg);
    cp.position.y = h * 0.92;
    cp.scale.y = 0.65;
    const gl = M(new THREE.CircleGeometry(h * 0.34, 9), gill, cg);
    gl.rotation.x = Math.PI / 2;
    gl.position.y = h * 0.93;
  };

  /* ---------- the floor round her: the torn cup, threads of mycelium, her brood ---------- */
  lathe([[0.02, 0], [0.7, 0], [0.78, 0.12], [0.7, 0.3], [0.62, 0.38], [0.6, 0.3]], 12, fleshD, g, 0.18);
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.2;
    const r1 = 0.65, r2 = 1.15 + rng() * 0.35;
    k4.tube(g, [V(Math.cos(a) * r1, 0.05, Math.sin(a) * r1), V(Math.cos(a + 0.15) * (r1 + r2) / 2, 0.03, Math.sin(a + 0.15) * (r1 + r2) / 2), V(Math.cos(a + 0.05) * r2, 0.015, Math.sin(a + 0.05) * r2)], 0.025, fleshD, 4);
  }
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.5;
    const r = 0.95 + rng() * 0.3;
    child(g, Math.cos(a) * r, Math.sin(a) * r, 0.22 + rng() * 0.2, 0.25);
  }
  glowSprite(g, '#4fd6ff', 2.4, V(0, 0.15, 0), 0.12);

  /* ---------- the stalk: swollen, fibrous, with a sleeping face grown into it ---------- */
  const stalkB = joint(g, V(0, 0.25, 0), 'stalk');
  const sk = stalkB.abs;
  lathe([[0.56, 0.2], [0.6, 0.45], [0.55, 0.8], [0.46, 1.2], [0.42, 1.55], [0.45, 1.85], [0.38, 2.0]], 14, flesh, sk, 0.06);
  // Fibres running up it.
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const r = (y: number): number => (y < 0.8 ? 0.58 : y < 1.4 ? 0.46 : 0.44) + 0.005;
    k4.tube(sk, [0.3, 0.9, 1.5, 1.9].map((y) => V(Math.cos(a + y * 0.15) * r(y), y, Math.sin(a + y * 0.15) * r(y))), 0.014, fleshD, 3);
  }
  // The face: heavy shut lids, a long nose of flesh, a slit of a mouth breathing light.
  for (const s of [-1, 1]) {
    const lid = M(new THREE.SphereGeometry(0.1, 8, 5), flesh, sk);
    lid.position.set(s * 0.15, 1.38, 0.4);
    lid.scale.set(1.2, 0.55, 0.6);
    lid.rotation.z = s * -0.25;
    const hol = M(new THREE.SphereGeometry(0.11, 8, 5), fleshD, sk);
    hol.position.set(s * 0.15, 1.33, 0.38);
    hol.scale.set(1.25, 0.7, 0.5);
    const lash = M(new THREE.TorusGeometry(0.09, 0.012, 3, 8, Math.PI), fleshD, sk);
    lash.position.set(s * 0.15, 1.36, 0.45);
    lash.rotation.set(0, 0, Math.PI + s * -0.25);
  }
  const nose = M(new THREE.ConeGeometry(0.06, 0.24, 6), flesh, sk);
  nose.position.set(0, 1.24, 0.46);
  nose.rotation.x = 0.25;
  const mouth = M(new THREE.SphereGeometry(0.11, 8, 5), gill, sk);
  mouth.position.set(0, 1.04, 0.42);
  mouth.scale.set(1, 0.18, 0.4);
  glowSprite(sk, '#4fd6ff', 0.45, V(0, 1.04, 0.5), 0.5);
  // Shelf fungi stepping up her side, lit underneath.
  for (const [a, y, r] of [[2.3, 0.7, 0.2], [2.6, 0.95, 0.15], [-0.9, 0.55, 0.17]] as const) {
    const sh = M(new THREE.SphereGeometry(r, 9, 4, 0, Math.PI * 2, 0, Math.PI / 2), cap, sk);
    sh.position.set(Math.cos(a) * 0.55, y, Math.sin(a) * 0.55);
    sh.scale.set(1, 0.35, 1);
    const ug = M(new THREE.CircleGeometry(r * 0.95, 9), gill, sk);
    ug.rotation.x = Math.PI / 2;
    ug.position.set(Math.cos(a) * 0.55, y - 0.005, Math.sin(a) * 0.55);
  }
  // The skirt under the cap: a ragged hanging ring.
  {
    const geo = new THREE.CylinderGeometry(0.5, 0.78, 0.38, 16, 1, true);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) if (pos.getY(i) < 0) pos.setY(i, pos.getY(i) + (i % 2 ? 0.1 : -0.04) + rng() * 0.06);
    geo.computeVertexNormals();
    const skirt = M(geo, std('#a6b2ba', { roughness: 0.8, side: THREE.DoubleSide, bumpScale: 0.05 }), sk);
    skirt.position.set(0, 1.7, 0);
  }

  /* ---------- the cap: vast, slate blue, crusted with warts; the gills burning underneath ---------- */
  const capB = joint(sk, V(0, 2.0, 0), 'cap');
  const cp = capB.abs;
  // A hunched bell, its rim wavy and curling under.
  lathe([[1.22, 1.81], [1.38, 1.79], [1.44, 1.86], [1.36, 1.99], [1.15, 2.24], [0.8, 2.52], [0.35, 2.7], [0.001, 2.74]], 20, cap, cp, 0.07);
  // The gills' light showing at the lip all round.
  const lip = M(new THREE.TorusGeometry(1.3, 0.05, 4, 28), gill, cp);
  lip.rotation.x = Math.PI / 2;
  lip.position.y = 1.8;
  // The gills: a glowing underside and fins radiating to the rim.
  const under = M(new THREE.RingGeometry(0.3, 1.3, 26, 1), gill, cp);
  under.rotation.x = Math.PI / 2;
  under.position.set(0, 1.84, 0);
  for (let i = 0; i < 20; i++) {
    const a = (i / 20) * Math.PI * 2;
    const f = M(new THREE.BoxGeometry(0.012, 0.1, 1.0), fleshD, cp);
    f.position.set(Math.cos(a) * 0.82, 1.84, Math.sin(a) * 0.8);
    f.rotation.y = -a + Math.PI / 2;
  }
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.4;
    glowSprite(cp, '#4fd6ff', 1.0, V(Math.cos(a) * 0.95, 1.7, Math.sin(a) * 0.95), 0.35);
  }
  glowSprite(cp, '#4fd6ff', 2.2, V(0, 1.75, 0), 0.25);
  // Warts over the top, bigger toward the crown, and patches of cyan light between them.
  const PROF: [number, number][] = [[0, 2.74], [0.35, 2.7], [0.8, 2.52], [1.15, 2.24], [1.36, 1.99], [1.44, 1.86]];
  const capY = (r: number): number => {
    for (let i = 1; i < PROF.length; i++) {
      const [r0, y0] = PROF[i - 1]!, [r1, y1] = PROF[i]!;
      if (r <= r1) return y0 + ((y1 - y0) * (r - r0)) / (r1 - r0);
    }
    return 1.86;
  };
  for (let i = 0; i < 26; i++) {
    const u = 0.12 + Math.sqrt(rng()) * 0.8, a = rng() * Math.PI * 2;
    const r = 1.3 * u;
    const lit = i % 3 === 0;
    const w = M(lit ? new THREE.SphereGeometry(1, 6, 4) : new THREE.DodecahedronGeometry(1, 0), lit ? gill : wart, cp);
    const sz = lit ? 0.05 + rng() * 0.04 : 0.04 + (1 - u) * 0.07;
    w.position.set(Math.cos(a) * r, capY(r) - 0.01, Math.sin(a) * r);
    w.scale.set(sz, sz * 0.45, sz);
    w.lookAt(w.position.clone().multiplyScalar(2).setY(w.position.y + 1.5));
  }
  // Spores drifting off the rim: a few motes of mist.
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 1;
    const m = M(new THREE.SphereGeometry(0.06 + rng() * 0.05, 5, 4), mist, cp);
    m.position.set(Math.cos(a) * 1.25, 1.55 - rng() * 0.3, Math.sin(a) * 1.25);
  }

  /* ---------- the ring of spores the burst throws out (built tiny) ---------- */
  const ringB = joint(g, V(0, 0, 0), 'ring');
  const rg = G(ringB.abs);
  rg.scale.setScalar(o?.burst ? 1 : SPORE_RING_REST);
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const r = 2.0 + (i % 2) * 0.2;
    const puff = M(new THREE.SphereGeometry(0.42 + (i % 3) * 0.08, 7, 5), mist, rg);
    puff.position.set(Math.cos(a) * r, 0.35 + (i % 2) * 0.12, Math.sin(a) * r);
    puff.scale.set(1.3, 0.75, 1.3);
    glowSprite(rg, '#4fd6ff', 1.3, V(Math.cos(a) * r, 0.42, Math.sin(a) * r), 0.35);
  }

  return done(c, SPORE_MOTHER_HEIGHT, () => undefined, undefined, bonesOf(c, { stalk: stalkB.p, cap: capB.p, ring: ringB.p }));
};
